/**
 * Protected Route Component
 *
 * Wraps routes that require authentication.
 * Redirects to login if user is not authenticated.
 *
 * DEV mode: Auth is skipped by default. Set VITE_FORCE_AUTH=true in .env.local
 * to enable auth checking during development (for testing the login flow).
 */

import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../core/auth";
import "../styles/palette.css";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, loading, isConfigured } = useAuth();
  const location = useLocation();

  // DEV mode: skip auth unless VITE_FORCE_AUTH is set
  if (import.meta.env.DEV && import.meta.env.VITE_FORCE_AUTH !== "true") {
    return <>{children}</>;
  }

  // Show nothing while loading auth state
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          backgroundColor: "var(--bg-page)",
          color: "var(--text-secondary)",
          fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
        }}
      >
        Loading...
      </div>
    );
  }

  // If Supabase is not configured, allow access
  if (!isConfigured) {
    console.warn(
      "Supabase not configured - allowing access without authentication",
    );
    return <>{children}</>;
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // User is authenticated, render the protected content
  return <>{children}</>;
}

export default ProtectedRoute;
